import { useCallback, useEffect, useState } from "react";
import axiosAdmin from "../../../../api/apiAdmin";
import { successToast, errorToast } from "../../toastAlerts/ToastAlerts";
import { apiError } from "../../../../api/apiError";
import {
  BsEnvelope,
  BsPhone,
  BsArrowClockwise,
  BsCheckCircle,
  BsXCircle,
  BsChevronDown,
} from "react-icons/bs";

/**
 * Send an email or SMS directly, and show what the provider actually answered.
 *
 * The panel deliberately surfaces the raw SMTP conversation rather than a
 * green tick: a message the SMTP server accepts can still be dropped later,
 * and only the provider's own reply tells them apart. Every send is recorded
 * in the log below so "did it go out?" can be checked afterwards.
 */
const DirectSendPanel = () => {
  const [form, setForm] = useState({
    channel: "email",
    recipient: "",
    subject: "",
    text: "",
  });
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  const [log, setLog] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [filters, setFilters] = useState({ q: "", channel: "", ok: "" });
  const [openRow, setOpenRow] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadLog = useCallback(async () => {
    try {
      const params = { per_page: 25 };
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "") params[k] = v;
      });
      const { data } = await axiosAdmin.get("/messages/log", { params });
      setLog(data.data || []);
      setLogTotal(data.total ?? 0);
    } catch (err) {
      errorToast(apiError(err, "ლოგი ვერ ჩაიტვირთა"));
    }
  }, [filters]);

  useEffect(() => {
    loadLog();
  }, [loadLog]);

  const send = async () => {
    if (!form.recipient || !form.text) {
      errorToast("შეავსეთ მიმღები და ტექსტი");
      return;
    }
    setSending(true);
    setResults(null);
    try {
      const { data } = await axiosAdmin.post("/messages/send", form);
      setResults(data.results);
      successToast("გაიგზავნა");
    } catch (err) {
      // A refusal still carries per-channel detail — show it rather than a toast alone.
      const d = err?.response?.data;
      if (d?.results) setResults(d.results);
      errorToast(apiError(err, "გაგზავნა ვერ მოხერხდა"));
    }
    setSending(false);
    loadLog();
  };

  const Badge = ({ ok }) =>
    ok ? (
      <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-[11px] font-bold">
        <BsCheckCircle className="inline mb-0.5" /> გაიგზავნა
      </span>
    ) : (
      <span className="text-red-700 bg-red-100 px-2 py-0.5 rounded-full text-[11px] font-bold">
        <BsXCircle className="inline mb-0.5" /> ჩავარდა
      </span>
    );

  return (
    <div className="flex flex-col gap-4">
      {/* Compose */}
      <div className="bg-white dark:bg-slate-700 rounded-lg p-5">
        <h3 className="font-bold dark:text-white mb-3">
          პირდაპირი გაგზავნა (მეილი / SMS)
        </h3>

        <div className="flex flex-wrap gap-2 mb-3">
          {[
            ["email", "მეილი", <BsEnvelope key="e" />],
            ["sms", "SMS", <BsPhone key="s" />],
            ["both", "ორივე", null],
          ].map(([key, label, icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => set("channel", key)}
              className={`px-4 h-9 rounded-md text-sm font-bold flex items-center gap-2 ${
                form.channel === key
                  ? "bg-teal_600 text-white"
                  : "bg-slate-200 dark:bg-slate-600 dark:text-white text-slate-700"
              }`}
            >
              {icon} {label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-1 gap-3 mb-3">
          <label className="text-xs flex flex-col gap-1">
            <span className="text-slate-500 dark:text-slate-300">
              მიმღები {form.channel === "sms" ? "(ტელეფონი)" : "(მეილი)"}
            </span>
            <input
              className="h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-md
                         dark:bg-slate-800 dark:text-white"
              placeholder={form.channel === "sms" ? "5XXXXXXXX" : "name@example.com"}
              value={form.recipient}
              onChange={(e) => set("recipient", e.target.value)}
            />
          </label>

          {form.channel !== "sms" && (
            <label className="text-xs flex flex-col gap-1">
              <span className="text-slate-500 dark:text-slate-300">სათაური</span>
              <input
                className="h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-md
                           dark:bg-slate-800 dark:text-white"
                placeholder="MGL Parcel"
                value={form.subject}
                onChange={(e) => set("subject", e.target.value)}
              />
            </label>
          )}
        </div>

        <textarea
          className="w-full h-28 p-3 text-sm border border-slate-300 dark:border-slate-600
                     rounded-md dark:bg-slate-800 dark:text-white mb-3"
          placeholder="ტექსტი"
          value={form.text}
          onChange={(e) => set("text", e.target.value)}
        />

        <button
          onClick={send}
          disabled={sending}
          className="rounded-md bg-teal_600 text-white text-sm font-bold px-6 h-10 disabled:opacity-50"
        >
          {sending ? "იგზავნება…" : "გაგზავნა"}
        </button>

        {/* What the provider said */}
        {results && (
          <div className="mt-4 flex flex-col gap-3">
            {results.map((r, i) => (
              <div
                key={i}
                className={`rounded-md border p-3 ${
                  r.ok
                    ? "border-green-200 bg-green-50"
                    : "border-red-200 bg-red-50"
                }`}
              >
                <div className="flex items-center gap-2 mb-2 text-sm">
                  <b>{r.channel === "sms" ? "SMS" : "მეილი"}</b>
                  <span className="text-slate-500">→ {r.recipient}</span>
                  <Badge ok={r.ok} />
                </div>
                {r.error && (
                  <p className="text-xs text-red-700 mb-2">{r.error}</p>
                )}
                {r.response && (
                  <>
                    <p className="text-[11px] text-slate-500 mb-1">
                      სერვერის პასუხი:
                    </p>
                    <pre className="text-[11px] font-mono bg-white/70 border border-slate-200
                                    rounded p-2 overflow-x-auto max-h-64 whitespace-pre-wrap">
                      {r.response}
                    </pre>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Log */}
      <div className="bg-white dark:bg-slate-700 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold dark:text-white">
            გაგზავნილი შეტყობინებები
            <span className="text-slate-400 font-normal"> ({logTotal})</span>
          </h3>
          <button
            onClick={loadLog}
            className="text-slate-500 hover:text-teal_600"
            title="განახლება"
          >
            <BsArrowClockwise />
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-3 text-xs">
          <input
            className="h-8 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       dark:bg-slate-800 dark:text-white flex-1 min-w-[180px]"
            placeholder="ძებნა: მიმღები / სათაური / ტექსტი"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          />
          <select
            className="h-8 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       dark:bg-slate-800 dark:text-white"
            value={filters.channel}
            onChange={(e) => setFilters((f) => ({ ...f, channel: e.target.value }))}
          >
            <option value="">ყველა არხი</option>
            <option value="email">მეილი</option>
            <option value="sms">SMS</option>
          </select>
          <select
            className="h-8 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       dark:bg-slate-800 dark:text-white"
            value={filters.ok}
            onChange={(e) => setFilters((f) => ({ ...f, ok: e.target.value }))}
          >
            <option value="">ყველა შედეგი</option>
            <option value="1">გაგზავნილი</option>
            <option value="0">ჩავარდნილი</option>
          </select>
        </div>

        {log.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">ჩანაწერი არ არის</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500 border-b border-slate-200 dark:border-slate-600">
                <tr>
                  <th className="text-left py-2 pr-2">თარიღი</th>
                  <th className="text-left py-2 pr-2">არხი</th>
                  <th className="text-left py-2 pr-2">მიმღები</th>
                  <th className="text-left py-2 pr-2">სათაური</th>
                  <th className="text-left py-2 pr-2">შედეგი</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {log.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-slate-100 dark:border-slate-600 align-top"
                  >
                    <td className="py-2 pr-2 dark:text-slate-200 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="py-2 pr-2 dark:text-slate-200">
                      {r.channel === "sms" ? "SMS" : "მეილი"}
                    </td>
                    <td className="py-2 pr-2 dark:text-slate-200">{r.recipient}</td>
                    <td className="py-2 pr-2 dark:text-slate-200">
                      {r.subject || "—"}
                    </td>
                    <td className="py-2 pr-2">
                      <Badge ok={r.ok} />
                      {r.error && (
                        <div className="text-red-600 mt-1 max-w-[260px]">
                          {r.error}
                        </div>
                      )}
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => setOpenRow(openRow === r.id ? null : r.id)}
                        className="text-slate-500 hover:text-teal_600"
                        title="სერვერის პასუხი"
                      >
                        <BsChevronDown />
                      </button>
                      {openRow === r.id && (
                        <pre className="text-[11px] font-mono bg-slate-50 dark:bg-slate-800
                                        dark:text-slate-200 border border-slate-200
                                        dark:border-slate-600 rounded p-2 mt-2 max-h-64
                                        overflow-auto whitespace-pre-wrap min-w-[320px]">
                          {r.response || "(პასუხი არ ჩაწერილა)"}
                          {"\n\n--- ტექსტი ---\n"}
                          {r.body}
                        </pre>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectSendPanel;
